import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';

interface UseUnsavedChangesWarningOptions {
  /**
   * Whether the form has unsaved changes
   */
  isDirty: boolean;
  /**
   * Custom message to show in the warning dialog
   * @default "¿Estás seguro de que quieres salir? Los cambios no guardados se perderán."
   */
  message?: string;
}

/**
 * Hook to warn users before leaving a page with unsaved changes.
 * Handles both React Router navigation (including browser back/forward) and browser refresh/close.
 *
 * @param options - Configuration options
 * @param options.isDirty - Whether the form has unsaved changes
 * @param options.message - Optional custom warning message
 *
 * @example
 * ```tsx
 * const form = useForm();
 * useUnsavedChangesWarning({
 *   isDirty: form.formState.isDirty,
 * });
 * ```
 */
export function useUnsavedChangesWarning({
  isDirty,
  message = '¿Estás seguro de que quieres salir? Los cambios no guardados se perderán.',
}: UseUnsavedChangesWarningOptions): void {
  const history = useHistory();

  useEffect(() => {
    if (!isDirty) {
      return;
    }

    // Block React Router navigation (including browser back/forward)
    const unblock = history.block(() => {
      return message;
    });

    // Handle browser refresh/close
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = message;
      return e.returnValue;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      unblock();
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [history, isDirty, message]);
}
